import { Box } from "@repo/components/src/ui/box";
import { useCallback, useMemo } from "react";
import {
  Avatar,
  AvatarProps,
  Bubble,
  BubbleProps,
  MessageProps,
  SystemMessage,
  utils,
} from "@kesha-antonov/react-native-chat";
import { HStack } from "../../../../ui/hstack";
import { MontuMessage } from "../../types";

const { isSameUser } = utils;

const isSameDay = (
  current: { createdAt?: string | number | Date },
  diff: { createdAt?: string | number | Date } | null | undefined,
) => {
  if (!diff || !diff.createdAt || !current.createdAt) return false;
  const a = new Date(current.createdAt);
  const b = new Date(diff.createdAt);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

type Props = MessageProps<MontuMessage> & {
  showAlwaysOnLeft?: boolean;
};

export default function GiftedChatMessage(props: Props) {
  const { showAlwaysOnLeft = true } = props;
  const position = showAlwaysOnLeft ? "left" : props.position;
  const renderSystemMessage = useCallback(() => {
    const { containerStyle, onMessageLayout, ...systemProps } = props;
    if (props.renderSystemMessage)
      return props.renderSystemMessage(systemProps);
    return <SystemMessage {...systemProps} />;
  }, [props]);
  const renderBubble = useCallback(() => {
    const bubbleProps: BubbleProps<MontuMessage> = {
      ...props,
      position,
    };

    if (props.renderBubble) return props.renderBubble(bubbleProps);

    return <Bubble {...bubbleProps} />;
  }, [props, position]);
  const renderAvatar = useCallback(() => {
    let extraStyle;
    if (
      isSameUser(props.currentMessage, props.previousMessage) &&
      isSameDay(props.currentMessage, props.previousMessage)
    )
      // Set the invisible avatar height to 0, but keep the width, padding, etc.
      extraStyle = { height: 0 };

    const avatarProps: AvatarProps<MontuMessage> = {
      ...props,
      position,
    };

    if (props.renderAvatar) return props.renderAvatar(avatarProps);

    return (
      <Avatar
        {...avatarProps}
        imageStyle={{
          left: [
            {
              height: 40,
              width: 40,
              borderRadius: 3,
            },
            extraStyle,
          ],
        }}
      />
    );
  }, [props, position]);
  const marginBottom = useMemo(
    () => (isSameUser(props.currentMessage, props.nextMessage) ? 8 : 25),
    [props],
  );
  return props.currentMessage.system ? (
    renderSystemMessage()
  ) : (
    <Box>
      <HStack
        className={`items-start justify-start max-w-[90%] web:select-text ${
          position === "right" ? "flex-row-reverse ml-auto" : "flex-row mr-auto"
        }`}
        style={{ marginBottom }}
      >
        {renderAvatar()}
        {renderBubble()}
      </HStack>
    </Box>
  );
}
